// ─── Canonical public URL for the browser app ──────────────────────────────
//
// SINGLE SOURCE OF TRUTH for the frontend. Mirrors
// `supabase/functions/_shared/site-url.ts` on the backend.
//
// Resolution order:
//   1. VITE_SITE_URL / VITE_APP_URL  — set this only if absolute URLs must
//      point at the production domain even when rendered elsewhere.
//   2. window.location.origin        — the domain the visitor is actually on.
//
// Defaulting to `window.location.origin` is what makes the app
// domain-agnostic: preview, *.lovable.app and any custom domain each generate
// correct absolute URLs with zero configuration. Attaching a new custom domain
// requires no code change here.

const configured =
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  (import.meta.env.VITE_APP_URL as string | undefined);

/** The one production host. Legacy/`www` hosts canonicalize to this. */
const PRIMARY_ORIGIN = "https://silversagehomeschoolhubs.com";

/** Hosts that must never appear in a canonical/og:url — they only redirect. */
const LEGACY_HOSTS = new Set([
  "www.silversagehomeschoolhubs.com",
  "bouldercityhomeschoolhub.com",
  "www.bouldercityhomeschoolhub.com",
]);

function normalize(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** Origin of the running app, e.g. `https://silversagehomeschoolhubs.com`. */
export function siteUrl(): string {
  if (configured) return normalize(configured);
  if (typeof window !== "undefined") {
    if (LEGACY_HOSTS.has(window.location.hostname)) return PRIMARY_ORIGIN;
    return window.location.origin;
  }
  return "";
}


/** Absolute URL for a path: `absoluteUrl("/classes")`. */
export function absoluteUrl(path = "/"): string {
  return `${siteUrl()}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Canonical URL for the current route (query and hash stripped). */
export function canonicalUrl(pathname?: string): string {
  const path =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  return absoluteUrl(path === "/" ? "" : path.replace(/\/+$/, "")) || "/";
}
